/**
 * Highcharts runtime adapter.
 *
 * Why this file exists:
 * - `highcharts/es-modules/masters/*` imports break in some consumers/tests where
 *   dependencies are executed as non-ESM (for example Jest or Angular test setups).
 * - Different bundlers expose Highcharts either on `globalThis.Highcharts` or as a
 *   module export.
 *
 * This adapter centralizes runtime resolution and module initialization so chart
 * components can import Highcharts from one stable internal path.
 */
// TODO GUI-4447 Do not remove .js extension from here, it uses karma resolver.  Karma task done on 09.09.2026, GUI-4447 can be taken into work.
import * as highstockModule from 'highcharts/highstock.js';
import * as highchartsMoreModule from 'highcharts/highcharts-more.js';
import * as highchartsSolidGaugeModule from 'highcharts/modules/solid-gauge.js';
import * as highchartsAccessibilityModule from 'highcharts/modules/accessibility.js';
import * as highchartsExportingModule from 'highcharts/modules/exporting.js';
import * as highchartsDataModule from 'highcharts/modules/data.js';
import type { HighchartsRuntime } from '../components/charts.types.js';

interface ModuleNamespace {
    default?: unknown;
}

const isHighchartsRuntime = (value: unknown): value is HighchartsRuntime => {
    return (
        !!value &&
        typeof value === 'object' &&
        'Chart' in value &&
        'StockChart' in value
    );
};

const resolveHighchartsRuntime = (): HighchartsRuntime => {
    const globalCandidate = (
        globalThis as typeof globalThis & { Highcharts?: unknown }
    ).Highcharts;

    if (isHighchartsRuntime(globalCandidate)) {
        return globalCandidate;
    }

    const moduleCandidate =
        (highstockModule as ModuleNamespace).default ?? highstockModule;

    if (isHighchartsRuntime(moduleCandidate)) {
        return moduleCandidate;
    }

    throw new Error('Unable to resolve Highcharts runtime.');
};

const initializeHighchartsModule = (moduleNamespace: ModuleNamespace): void => {
    const initializer = moduleNamespace.default ?? moduleNamespace;

    if (typeof initializer === 'function') {
        (initializer as (runtime: HighchartsRuntime) => void)(Highcharts);
    }
};

export const Highcharts = resolveHighchartsRuntime();

initializeHighchartsModule(highchartsMoreModule);
initializeHighchartsModule(highchartsSolidGaugeModule);
initializeHighchartsModule(highchartsAccessibilityModule);
initializeHighchartsModule(highchartsExportingModule);
initializeHighchartsModule(highchartsDataModule);

export default Highcharts;
